import { forwardRef, type JSX, type ReactNode } from 'react';

import {
  type StylingProps,
  MaskingProps,
} from '@dynatrace/strato-components/core';

import { CategoricalBarChart } from './CategoricalBarChart.js';
import { CategoricalBarChartLegend } from './components/legend/CategoricalBarChartLegend.js';
import { useCategoricalBarChartBaseConfig } from './hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartData } from './hooks/useCategoricalBarChartData.js';
import { CategoricalBarChartLegendConfiguration } from './types/categorical-bar-chart.js';
import { isStacked, isGrouped } from './utils/layout.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';

interface CategoricalBarChartLayoutProps extends StylingProps, MaskingProps {
  emptyState?: ReactNode;
  errorState: ReactNode | ((errorMessage: string) => JSX.Element) | undefined;
  legend: CategoricalBarChartLegendConfiguration;
  isEmpty: boolean;
  loading: boolean;
  height?: string;
  width?: number;
}

export const CategoricalBarChartLayout = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  CategoricalBarChartLayoutProps
>((props, forwardedRef) => {
  const {
    height,
    width,
    legend,
    isEmpty,
    loading,
    errorState,
    emptyState,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;
  const { uniqueDimensions, categoricalData } = useCategoricalBarChartData();
  const { groupMode } = useCategoricalBarChartBaseConfig();

  const canShowLegend =
    isGrouped(groupMode) ||
    (isStacked(groupMode) && uniqueDimensions.length > 1);
  const {
    position: legendPosition,
    ratio: legendRatio,
    onRatioChange,
  } = legend;

  const showLegend = canShowLegend && !legend.hidden;
  const chartLayoutRef = useAutoLegendRefresh(categoricalData);

  return (
    <ChartLayout
      ref={chartLayoutRef}
      chartHeight={height}
      chartWidth={width}
      isEmpty={isEmpty}
      emptyState={emptyState}
      errorState={errorState}
      showLoader={loading}
      className={className}
      style={style}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
    >
      <ChartLayout.Graph>
        <CategoricalBarChart ref={forwardedRef} />
      </ChartLayout.Graph>
      {showLegend && (
        <ChartLayout.Legend
          position={legendPosition}
          ratio={legendRatio}
          onResize={onRatioChange}
        >
          <CategoricalBarChartLegend />
        </ChartLayout.Legend>
      )}
    </ChartLayout>
  );
});

(
  CategoricalBarChartLayout as typeof CategoricalBarChartLayout & {
    displayName: string;
  }
).displayName = 'CategoricalBarChartLayout';
